<script setup lang="ts">
import {Keyboard, Mousewheel, Virtual} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import type {Post} from "~/repository/types";

const props = withDefaults(defineProps<{
  posts: { [key: string]: Post },
  skeleton?: {
    count: number
  },
  fetching: boolean
}>(), {
  skeleton(props) {
    return {
      count: 12
    }
  },
})
const swiperRef = ref<typeof Swiper | null>(null);
const fullScreenMode = ref(false);
const {isLoggedIn} = useAuthStore()
const emit = defineEmits(['delete:post'])
const swiperConfig = {
  virtual: {
    cache: true,
    addSlidesBefore: 5,
    addSlidesAfter: 5
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  mousewheel: {
    forceToAxis: true,
    sensitivity: 0.5,
    thresholdDelta: 30
  }
}


function handleSlideClick(post: Post) {
  if (!isLoggedIn) {
    throw createError({
      status: 401
    })
  }
  const postsArray = Object.values(props.posts);
  const index = postsArray.findIndex((postItem) => postItem.id === post.id);
  if (index !== -1) {
    fullScreenMode.value = true;
    document.body.classList.add('no-scroll');
    nextTick(() => {
      swiperRef.value?.slideTo(index, 0);
    });
  }
}

function onSwiper(swiper: typeof Swiper) {
  swiperRef.value = swiper;
}

function closeFullScreen() {
  fullScreenMode.value = false;
  document.body.classList.remove('no-scroll');
}

watch(fullScreenMode, (newVal) => {
  if (newVal) {
    history.pushState({fullScreen: true}, "", `#fullscreen`);
  } else {
    if (history.state?.fullScreen) {
      history.back();
    }
  }
});

window.addEventListener("popstate", () => {
  if (history.state?.fullScreen) {
    fullScreenMode.value = true;
    document.body.classList.add("no-scroll");
  } else {
    fullScreenMode.value = false;
    document.body.classList.remove("no-scroll");
  }
});

function proxyPostDeletion(post: Post) {
  closeFullScreen()
  emit('delete:post', post)
}

</script>

<template>
  <div class="grid w-full gap-px grid-cols-3">
    <PostCard v-for="(post, key) in posts" :post="post" :key="`profile_${key}`" @click="handleSlideClick(post)"/>
    <ProfilePostSkeleton v-if="fetching" :count="skeleton.count"/>
  </div>
  <teleport to="body">
    <div v-if="fullScreenMode"
         class="fixed inset-0 bg-background z-[60] min-h-svh flex items-start md:items-center justify-center">
      <section class="full-screen-post aspect-[9/16]  px-0 max-md:w-full max-md:h-full md:px-6 mx-auto max-h-svh">
        <swiper ref="swiperRef" @swiper="onSwiper" :modules="[Keyboard, Mousewheel, Virtual]"
                :virtual="swiperConfig.virtual"
                :spaceBetween="0"
                :mousewheel="swiperConfig.mousewheel" :keyboard="true"
                :noSwipingSelector="'.post-message'"
                :direction="'vertical'" :nested="true" :touchReleaseOnEdges="true"
                class="max-h-svh w-full">
          <swiper-slide v-for="(post, id, index) in posts" :key="id" :virtualIndex="index"
                        class="md:py-5 h-full w-full">
            <Post :key="id" :post="post" @delete:post="proxyPostDeletion"/>
          </swiper-slide>
        </swiper>
      </section>
      <button @click="closeFullScreen"
              class="absolute top-4 left-4 md:top-5 rotate-180 z-[61] text-white md:text-foreground cursor-pointer">
        <IconChevronRight/>
      </button>
    </div>
  </teleport>
</template>
